import { Controller, useFormContext } from 'react-hook-form'
import {
  CurecallInput,
  CurecallInputLabel,
  CurecallMultiSelect,
  CurecallSearchableSelect,
  CurecallTimePicker,
  TOption,
  TOptionWithNumberValue,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useId, useMemo, useState } from 'react'
import { ETriggerTiming, ETriggerUnit, TChatbotGeneralFormValues } from './chatbot-form.type'

type TTriggerUnitOption = TOption<ETriggerUnit>
type TTriggerTimingOption = TOption<ETriggerTiming>

export function ChatbotScheduleFields() {
  const { t } = useTranslation()
  const id = useId()
  const {
    formState: { errors, defaultValues },
    control,
    setValue,
    clearErrors,
  } = useFormContext<TChatbotGeneralFormValues>()

  const DEFAULT_TRIGGER_UNIT_OPTION = {
    label: t('pages.configuration.followups.chatbot.general-form.unit-days'),
    value: ETriggerUnit.DAYS,
  }
  const TRIGGER_UNIT_OPTIONS: TTriggerUnitOption[] = useMemo(
    () => [
      {
        label: t('pages.configuration.followups.chatbot.general-form.unit-hours'),
        value: ETriggerUnit.HOURS,
      },
      DEFAULT_TRIGGER_UNIT_OPTION,
      {
        label: t('pages.configuration.followups.chatbot.general-form.unit-weeks'),
        value: ETriggerUnit.WEEKS,
      },
      {
        label: t('pages.configuration.followups.chatbot.general-form.unit-months'),
        value: ETriggerUnit.MONTHS,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const TRIGGER_TIMING_OPTIONS: TTriggerTimingOption[] = useMemo(
    () => [
      {
        label: t('pages.configuration.followups.chatbot.general-form.timing-before'),
        value: ETriggerTiming.BEFORE,
      },
      {
        label: t('pages.configuration.followups.chatbot.general-form.timing-after'),
        value: ETriggerTiming.AFTER,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const ALLOWED_DAYS_OPTIONS: TOptionWithNumberValue[] = useMemo(
    () => [
      { label: t('common.day.monday'), value: 1 },
      { label: t('common.day.tuesday'), value: 2 },
      { label: t('common.day.wednesday'), value: 3 },
      { label: t('common.day.thursday'), value: 4 },
      { label: t('common.day.friday'), value: 5 },
      { label: t('common.day.saturday'), value: 6 },
      { label: t('common.day.sunday'), value: 7 },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const DEFAULT_ALLOWED_DAYS = new Set([1, 2, 3, 4, 5])

  const [selectedTriggerUnit, setSelectedTriggerUnit] = useState<TTriggerUnitOption>(
    defaultValues?.schedule?.triggerUnit
      ? (TRIGGER_UNIT_OPTIONS.find(
          ({ value }) => value === defaultValues?.schedule?.triggerUnit,
        ) as TTriggerUnitOption)
      : DEFAULT_TRIGGER_UNIT_OPTION,
  )
  const [selectedTriggerTiming, setSelectedTriggerTiming] = useState<TTriggerTimingOption>(
    defaultValues?.schedule?.triggerTiming
      ? (TRIGGER_TIMING_OPTIONS.find(
          ({ value }) => value === defaultValues?.schedule?.triggerTiming,
        ) as TTriggerTimingOption)
      : (TRIGGER_TIMING_OPTIONS[0] as TTriggerTimingOption),
  )
  const [selectedAllowedDays, setSelectedAllowedDays] = useState<TOptionWithNumberValue[]>(
    defaultValues?.schedule?.allowedDays
      ? ALLOWED_DAYS_OPTIONS.filter((day) => defaultValues?.schedule?.allowedDays?.includes(day.value))
      : ALLOWED_DAYS_OPTIONS.filter((day) => DEFAULT_ALLOWED_DAYS.has(day.value)),
  )

  const handleChangeTriggerUnit = (option: TTriggerUnitOption) => {
    setSelectedTriggerUnit(option)
    setValue('schedule.triggerUnit', option.value)
    clearErrors('schedule.triggerUnit')
  }

  const handleChangeTriggerTiming = (option: TTriggerTimingOption) => {
    setSelectedTriggerTiming(option)
    setValue('schedule.triggerTiming', option.value)
    clearErrors('schedule.triggerTiming')
  }

  const handleChangeAllowedDays = (options: TOptionWithNumberValue[]) => {
    setSelectedAllowedDays(options)
    setValue(
      'schedule.allowedDays',
      options.map((opt) => opt.value),
    )
    clearErrors('schedule.allowedDays')
  }

  const getSelectedDaysLabel = () => {
    const selectedValuesSet = new Set(selectedAllowedDays.map((day) => day.value))
    const isWeekdays =
      selectedValuesSet.size === DEFAULT_ALLOWED_DAYS.size &&
      [...DEFAULT_ALLOWED_DAYS].every((day) => selectedValuesSet.has(day))

    if (isWeekdays) {
      return t('pages.configuration.followups.chatbot.general-form.weekdays-label')
    }

    return (
      selectedAllowedDays.length + ' ' + t('pages.configuration.followups.chatbot.general-form.days-selected')
    )
  }

  return (
    <div className="c-flex c-gap-3">
      <fieldset id={id}>
        <CurecallInputLabel
          htmlFor={id}
          label={t('pages.configuration.followups.chatbot.general-form.trigger-schedule')}
          isRequired={true}
          tooltipContent={t('pages.configuration.followups.chatbot.general-form.trigger-schedule-tooltip')}
          tooltipPosition="right"
        />
        <div className="c-flex c-gap-3 c-items-end">
          <Controller
            control={control}
            name="schedule.triggerDelay"
            rules={{ required: true, min: 0 }}
            render={({ field }) => (
              <CurecallInput
                placeholder="0"
                value={field.value?.toString() || ''}
                onChange={(e) => field.onChange(Number.parseInt(e.target.value) || 0)}
                type="number"
                errorMessage={errors.schedule?.triggerDelay && t('common.form.required')}
                wrapperClassName="c-w-20"
              />
            )}
          />

          <Controller
            control={control}
            name="schedule.triggerUnit"
            rules={{ required: true }}
            render={() => (
              <CurecallSearchableSelect
                items={TRIGGER_UNIT_OPTIONS}
                onSelect={handleChangeTriggerUnit}
                defaultItemSelected={selectedTriggerUnit}
                placeholder={t('pages.configuration.followups.chatbot.general-form.select-unit')}
                wrapperClassName="c-w-36"
              />
            )}
          />

          <Controller
            control={control}
            name="schedule.triggerTiming"
            rules={{ required: true }}
            render={() => (
              <CurecallSearchableSelect
                items={TRIGGER_TIMING_OPTIONS}
                onSelect={handleChangeTriggerTiming}
                defaultItemSelected={selectedTriggerTiming}
                placeholder={t('pages.configuration.followups.chatbot.general-form.select-timing')}
                wrapperClassName="c-w-64"
              />
            )}
          />
        </div>
      </fieldset>

      <Controller
        control={control}
        name="schedule.sendAt"
        render={({ field }) => (
          <CurecallTimePicker
            label={t('pages.configuration.followups.chatbot.general-form.send-at')}
            defaultValue={field.value}
            onChangeTime={field.onChange}
          />
        )}
      />

      <Controller
        control={control}
        name="schedule.allowedDays"
        rules={{ required: true }}
        render={() => (
          <CurecallMultiSelect
            label={t('pages.configuration.followups.chatbot.general-form.allowed-days')}
            items={ALLOWED_DAYS_OPTIONS}
            selectedItems={selectedAllowedDays}
            onSelect={handleChangeAllowedDays}
            selectedElementsLabel={getSelectedDaysLabel()}
            placeholder={t('pages.configuration.followups.chatbot.general-form.select-days')}
            tooltipContent={t('pages.configuration.followups.chatbot.general-form.allowed-days-tooltip')}
            tooltipPosition="top-end"
            isRequired={true}
            errorMessage={
              errors.schedule?.allowedDays &&
              t('pages.configuration.followups.chatbot.general-form.allowed-days-error')
            }
            wrapperClassName="c-w-72 c-relative"
          />
        )}
      />
    </div>
  )
}
